import React, { useEffect, useRef, useState } from 'react'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import {
  PATIENTS_AGE_SERIES,
  PATIENT_STATUS_COLORS,
  PATIENT_STATUS_LEGEND_ORDER,
} from './operationsMockData'

const OPERATIONS_CHART_HEIGHT = 260

/** Room for top Y-axis tick labels (14px in operations bar chart CSS). */
const OPERATIONS_PATIENTS_CHART_MARGIN = {
  left: 0,
  right: 0,
  top: 14,
  bottom: 4,
} as const

/** Figma detail chart: 200px bars in ~233px category slots at full width. */
const FIGMA_STATUS_BAR_MAX_PX = 200
const FIGMA_STATUS_SLOT_BAR_RATIO = 200 / 233
const STATUS_CHART_Y_AXIS_WIDTH = 48
const STATUS_CHART_HORIZONTAL_PAD = 60

function usePatientStatusBarSizing(barCount: number): {
  wrapRef: React.RefObject<HTMLDivElement>
  barSize: number
  barCategoryGap: string
} {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [barSize, setBarSize] = useState(120)
  const [barCategoryGap, setBarCategoryGap] = useState('10%')

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return

    const update = (): void => {
      const width = el.clientWidth
      if (width <= 0) return
      const inner = Math.max(
        0,
        width - STATUS_CHART_Y_AXIS_WIDTH - STATUS_CHART_HORIZONTAL_PAD,
      )
      const slotWidth = inner / barCount
      const nextBar = Math.max(
        40,
        Math.min(
          FIGMA_STATUS_BAR_MAX_PX,
          Math.floor(slotWidth * FIGMA_STATUS_SLOT_BAR_RATIO),
        ),
      )
      setBarSize(nextBar)
      const gapPct = Math.max(
        6,
        Math.min(18, Math.round((1 - FIGMA_STATUS_SLOT_BAR_RATIO) * 100)),
      )
      setBarCategoryGap(`${gapPct}%`)
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [barCount])

  return { wrapRef, barSize, barCategoryGap }
}

type AxisConfig = { yAxisDomain: [number, number]; yAxisTicks: number[] }

type OperationsPatientsAgeChartProps = {
  data: Array<{ label: string; Male: number; Female: number }>
  axis: AxisConfig
}

export function OperationsPatientsAgeChart({
  data,
  axis,
}: OperationsPatientsAgeChartProps): React.ReactElement {
  return (
    <div className="dashboard-operations-bar-chart-wrap">
      <DashboardStackedBarChart
        data={data}
        series={PATIENTS_AGE_SERIES}
        horizontal={false}
        stacked={false}
        valueFormatter={(v) => String(v)}
        height={OPERATIONS_CHART_HEIGHT}
        yAxisDomain={axis.yAxisDomain}
        yAxisTicks={axis.yAxisTicks}
        yAxisWidth={48}
        yAxisTickMargin={0}
        margin={OPERATIONS_PATIENTS_CHART_MARGIN}
        showLegend={false}
        barSize={20}
        barGap={5}
        barCategoryGap="18%"
        className="dashboard-operations-bar-chart"
      />
      <div className="dashboard-operations-bar-chart-legend dashboard-operations-bar-chart-legend--age">
        {PATIENTS_AGE_SERIES.map((s) => (
          <span
            key={s.dataKey}
            className="dashboard-operations-bar-chart-legend-item"
          >
            <span
              className="dashboard-operations-bar-chart-legend-dot"
              style={{ background: s.color }}
            />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  )
}

type OperationsPatientStatusChartProps = {
  data: Array<{ label: string; value: number }>
  axis: AxisConfig
}

export function OperationsPatientStatusChart({
  data,
  axis,
}: OperationsPatientStatusChartProps): React.ReactElement {
  const { wrapRef, barSize, barCategoryGap } = usePatientStatusBarSizing(
    data.length,
  )

  return (
    <div
      ref={wrapRef}
      className="dashboard-operations-bar-chart-wrap dashboard-operations-bar-chart-wrap--status"
    >
      <DashboardStackedBarChart
        data={data}
        series={[{ dataKey: 'value', name: '', color: '' }]}
        horizontal={false}
        stacked={false}
        categoryIndicatorColors={PATIENT_STATUS_COLORS}
        hideCategoryIndicatorCircles
        hideCategoryAxisLabels
        valueFormatter={(v) => String(v)}
        height={OPERATIONS_CHART_HEIGHT}
        yAxisDomain={axis.yAxisDomain}
        yAxisTicks={axis.yAxisTicks}
        yAxisWidth={STATUS_CHART_Y_AXIS_WIDTH}
        yAxisTickMargin={0}
        margin={OPERATIONS_PATIENTS_CHART_MARGIN}
        showLegend={false}
        barSize={barSize}
        barCategoryGap={barCategoryGap}
        className="dashboard-operations-bar-chart dashboard-operations-bar-chart--status"
      />
      <div className="dashboard-operations-bar-chart-legend dashboard-operations-bar-chart-legend--status">
        {PATIENT_STATUS_LEGEND_ORDER.map((label) => (
          <span
            key={label}
            className="dashboard-operations-bar-chart-legend-item"
          >
            <span
              className="dashboard-operations-bar-chart-legend-dot"
              style={{ background: PATIENT_STATUS_COLORS[label] }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}
